// THE OVERLAY / CUTAWAY SHOT-TYPE CATALOGUE (Order 3.9994, spec §5g).
//
// Matt, 2026-08-08: *"Do we have a table of all the types of shots that are
// overlay shots and cutaway shots? ... we need to have that be where each of
// those types then has many variants produced ... when we are working in the
// scripting part of something and we determine that something needs to have,
// let's say, a two-word overlay, then there would be a two-word overlay
// template chosen. It would then use those two words on each of the
// resolutions to render them correctly."*
//
// WHAT WAS THERE BEFORE, AND WHY IT WAS NOT THIS. The Shot Overlays DB carries
// an `Overlay Kind` select with nine options — Diagram, Label, Step callout,
// Pull-quote, B-roll card, Lower third, Title card, Collage, Other — and a
// `Composite Role` of See-through / Full-frame card, which is the
// overlay/cutaway split under its older name. That is a VOCABULARY on an
// INSTANCE table, and the table holds no rows. It can label an overlay somebody
// already made; it cannot be used to CHOOSE one, because nothing in it reaches
// a layout. Choosing is the whole point, so this file is the catalogue and that
// select stays what it is: the label you put on a row afterwards.
//
// WHY IT LIVES HERE, and not in Notion or in the orchestrator:
//   · BESIDE THE TEMPLATES IT BINDS TO. A variant names a `CollageTemplateDef`
//     id and maps content into that template's own slot ids. Here, both are one
//     import away and a variant that names a dead template or a dead slot is a
//     TYPE ERROR at build time. In JSON it would be a runtime surprise, and in
//     Notion it would be a network call away from anything that could check it.
//   · ONE HOME. anim-schema is the shared contract four consumers already alias
//     (`@gooddesign/anim-schema`), so the scripting pass, the Compositor and the
//     render lane read the same catalogue rather than three copies of it.
//   · A PYTHON READER IS NOT BLOCKED. The survey checks already run TypeScript
//     through `tsx` and read JSON off stdout; a Python pass does the same. That
//     is one home read two ways, which is the opposite of two homes.
//
// NOT THE SAME THING AS THE NOTION "SHOT TYPES" DB. That one is the shot's
// PRODUCTION kind (`animated_idea`, a talking head, a title) and it drives
// `default_stage_pipeline` — see `orchestrator/dispatcher-storyboard/handlers/
// change_shot_type.py`. This one is what a COMPOSITED shot delivers and how it
// is laid out. Two different axes on the same noun; the exported names here all
// say overlay/cutaway so the two cannot be confused at a call site.
//
// ---------------------------------------------------------------------------
// THE GRAIN OF A TYPE — the decision this file had to make first
// ---------------------------------------------------------------------------
//
// Matt's example, *"a two-word overlay"*, is finer than any of the nine kinds:
// `Label` says what the shot DOES; "two-word" says what it CARRIES. So a type
// is a pair — a ROLE and a CONTENT SIGNATURE — and the rule that separates two
// types from one type plus a variant is this:
//
//     Two shapes are the SAME type iff a writer could hand the same content to
//     both without editing it. If naming it changes what the writer must
//     SUPPLY — a second line, an attribution, a number, a different count of
//     words — it is a different TYPE. If it only changes what the result LOOKS
//     LIKE, it is a VARIANT.
//
// WORD COUNT IS PART OF THE SIGNATURE, NOT COSMETICS. §5e is why: *"different
// text layouts moving from horizontal to vertical stacking of words."* One word
// cannot stack. Two words stack, one per line. Six words wrap as a run. The
// count decides the arrangement, so it decides the type — and a variant of
// `two-word-overlay` can honour it mechanically by mapping the two words to TWO
// template slots (see `slots`, below), which is a different recipe and not a
// different skin.
//
// THE NINE KINDS ARE KEPT AS THE ROLE HALF. Nothing here invents a taxonomy:
// every type carries the `kind` it came from, and the catalogue is those kinds
// fanned out along the axis Matt's own example adds. `Other` is the one that
// does not survive — an escape hatch on an instance table cannot pick a
// template, and a type that reaches no layout is precisely the thing this file
// exists to end.
//
// ---------------------------------------------------------------------------
// ROLE IS DERIVED, NEVER TAKEN ON TRUST
// ---------------------------------------------------------------------------
//
// §5d tells the three exports apart by *the solid-colour background*, and the
// kind is read off the stack rather than stored. The same rule holds one layer
// up: a CUTAWAY's template lays a full-frame opaque ground, an OVERLAY's does
// not, and `roleOfRecipe()` reads it back off the sampled recipe. A type
// DECLARES a role and the declaration is checked against what actually renders,
// so a type cannot call itself see-through while binding to a template that
// paints an opaque field over the camera.
//
// MEASURED 2026-08-08, and it is the shape of the whole catalogue: six of the
// eight templates lay a ground (title-card, banded-print, specimen, nk-backdrop,
// archival-parallax, map) and exactly two do not — `lower-third` and
// `nk-overlay`. So the studio's see-through surface is two templates, one of
// which carries text. Every text-bearing overlay type below therefore binds to
// `lower-third` and varies by band geometry, ink and word-split rather than by
// arrangement family. That is a real, visible thinness in the template set, not
// a modelling choice, and widening it is a DESIGN pass (Order 3.9993's remit —
// §5g "the review that has not happened"), not something a catalogue may invent.
// The same measurement is why there is no `diagram-overlay`: a see-through
// labelled figure has no template, and declaring the type anyway would be the
// exact failure Order 3.9991 caught — a declaration that reaches nothing.
//
// WHAT IS DELIBERATELY NOT HERE: the pass that CHOOSES a type for a beat. That
// is §7 [q-beat-treatment-is-proposed] / Order 3.9979 and it is blocked on Matt.
// This file is what that pass selects FROM.

import type { Aspect } from '../composition';
import type { CollageRecipe, RecipeLayer } from '../collage-recipe';
import type { AxisValue, CollageTemplateDef } from './types';
import { getCollageTemplate } from './defs';
import { sampleTemplate, sampleFrames, type SampleTemplateInput } from './sampler';
import { makeCollageRng } from '../collage';

// --- The two roles ----------------------------------------------------------
// All THREE of §5d's exports. Inset was left out of the first cut, reasoning
// that "an inset's camera placement is Matt's call in Resolve — there is no
// content signature a script could name for it." Matt corrected that on
// 2026-08-08: *"we need to have subtypes of each of our overlay, cutaway, and
// insert."* The original reasoning conflated two things, and his own description
// separates them cleanly:
//
//   THE CAMERA RECT   where the window sits and what shows through it. Still
//                     his, still framed in Resolve (frames.matt-frames-the-
//                     footage). No type here touches it.
//   THE CONTENT       *"the background solid and the foreground content
//                     layers"* composed AROUND that window. That has a signature
//                     exactly like an overlay's, and it is what a writer picks
//                     at scripting time.
//
// The role is still READ back off the composed stack, never trusted from the
// label — `roleOfRecipe` below, and the survey check asserts they agree.

export type ShotRole = 'overlay' | 'cutaway' | 'inset';

/** The skin a shot type renders in when the caller names none.
 *
 *  IT WAS `'default'`, WHICH IS NOT A SKIN. Measured 2026-08-08: `getCollageSkin`
 *  knows `pop-riso`, `nk-dusk` and `explainer-print`, so every one of the 15
 *  types produced a recipe that threw "unknown collage skin" the moment anything
 *  tried to COMPOSE it — 0 of 15 composable on the default, 15 of 15 on a real
 *  skin. It went unnoticed because the catalogue has no callers yet and its
 *  survey check stopped at the recipe, one step before the throw.
 *
 *  `explainer-print` is the expository house skin (Order 158.4), which is the
 *  film language this catalogue serves. A caller that wants another one passes
 *  it; what it must never do again is silently name a skin that does not exist. */
export const DEFAULT_SHOT_TYPE_SKIN = 'explainer-print';

/** The nine `Overlay Kind` options, minus `Other`. Kept verbatim so a catalogue
 *  type can always be traced back to the vocabulary it came from. */
export const OVERLAY_KINDS = [
  'Diagram', 'Label', 'Step callout', 'Pull-quote', 'B-roll card',
  'Lower third', 'Title card', 'Collage',
] as const;
export type OverlayKind = (typeof OVERLAY_KINDS)[number];

// --- The content signature ----------------------------------------------------
// What a writer must supply. This is the half of a type that Matt's "two-word"
// names, and the half a variant may not change.

export interface ShotContentSlot {
  id: string;
  /** Inclusive word bounds. `{ min: 2, max: 2 }` is Matt's two-word overlay. */
  words: { min: number; max: number };
  /** Inclusive LINE bounds — how many lines the writer supplies, separated by
   *  newlines. Absent means one, which is what every type filed before this
   *  meant, so none of them changes.
   *
   *  LINE COUNT IS SIGNATURE, NOT LOOK (Matt, 2026-08-08, spec §5g
   *  `types.signature-is-shape`): *"We have templates that are single-word, a
   *  single line, or two or three lines... Is it a graph? Is it bullet points
   *  vertically stacked?"* It passes this file's own test for a type rather than
   *  a variant — a writer cannot hand a one-line statement to a three-line
   *  template without editing it, because they have to write two more lines.
   *
   *  It is NOT the same as a long run wrapping. `phrase-overlay` already breaks
   *  across two lines in one of its variants; that is the renderer choosing
   *  where to fold six words the writer supplied as one line. This is the writer
   *  supplying three separate lines and meaning them to stay separate. */
  lines?: { min: number; max: number };
  /** Hard character ceiling. INVARIANT: never greater than the smallest
   *  `max_chars` among the template slots any variant maps this slot to — a
   *  type must not accept content one of its own variants cannot render. */
  max_chars: number;
  description: string;
}

// --- A variant ------------------------------------------------------------------
// One produced look of a type. Same content in, a different render out.

export interface ShotTypeVariant {
  id: string;
  name: string;
  /** A `CollageTemplateDef` id. */
  template: string;
  /** Content slot id → the template text slot ids it fills, IN ORDER.
   *
   *  ONE target puts the whole line in one slot. TWO OR MORE distribute the
   *  words across them — which is how `two-word-overlay` renders its two words
   *  on two lines rather than one, and is the mechanical form of §5e's
   *  "vertical stacking of words". Every template text slot a variant does NOT
   *  name is explicitly dropped at render time; an optional slot left unnamed
   *  would otherwise render its PLACEHOLDER into a finished frame. */
  slots: Record<string, readonly string[]>;
  /** Axis overrides on the template. Validated by `resolveTemplateAxes`. */
  axes?: Record<string, AxisValue>;
  description: string;
}

// --- A type ------------------------------------------------------------------------

export interface ShotType {
  id: string;
  name: string;
  role: ShotRole;
  /** Which of the nine this came from. */
  kind: OverlayKind;
  /** Empty for element-driven types (a frame, a field) that carry no language. */
  content: readonly ShotContentSlot[];
  description: string;
  /** MANY, never one: a type with a single variant is a rename of its template. */
  variants: readonly ShotTypeVariant[];
}

// =====================================================================================
// THE CATALOGUE
// =====================================================================================
//
// Ordered overlay-first, and within a role by how much language the type takes,
// so the list reads the way a writer scans it.

export const SHOT_TYPES: readonly ShotType[] = [
  {
    id: 'diagram-overlay',
    name: 'Diagram overlay',
    role: 'overlay',
    kind: 'Diagram',
    description:
      'THE TYPE THE CATALOGUE HAD TO DECLINE. A labelled figure over the '
      + 'footage — a part, a diagram, a photographed object, named. It could be '
      + 'described and not rendered until `figure-callout` existed, because every '
      + 'text-bearing overlay bound to `lower-third` and a lower third is '
      + 'furniture pinned to an edge, not a unit that sits beside the thing it '
      + 'names (claim see-through-templates).',
    content: [
      { id: 'label', words: { min: 1, max: 3 }, max_chars: 24,
        description: 'What the figure is. A name, not a sentence.' },
      { id: 'note', words: { min: 3, max: 9 }, max_chars: 56,
        description:
          'What it does, in a clause. Part of the SIGNATURE: a writer choosing '
          + 'this type is undertaking to supply both, which is what separates it '
          + 'from a bare label over a picture.' },
    ],
    variants: [
      { id: 'right', name: 'Figure right', template: 'figure-callout',
        slots: { label: ['label'], note: ['note'] },
        axes: { side: 'right', figure_scale: 0.26, plate_ink: 'dark', leader: true },
        description:
          'The callout takes the right; the left stays clear for the shot. The '
          + 'default, and the one to reach for when the subject sits left.' },
      { id: 'left', name: 'Figure left', template: 'figure-callout',
        slots: { label: ['label'], note: ['note'] },
        axes: { side: 'left', figure_scale: 0.26, plate_ink: 'dark', leader: true },
        description: 'Mirrored, for when the subject sits right of frame.' },
      { id: 'large-quiet', name: 'Large, no leader', template: 'figure-callout',
        slots: { label: ['label'], note: ['note'] },
        axes: { side: 'right', figure_scale: 0.34, plate_ink: 'secondary', leader: false },
        description:
          'A bigger figure and no rule — for when the figure is obviously the '
          + 'subject and a line pointing at it would be telling you twice.' },
    ],
  },


  // ===================================================================================
  // INSET — the camera is INSIDE the composition (Order 3.9995)
  // ===================================================================================
  //
  // Matt, 2026-08-08: *"I want to have multiple types of frames in frame... And
  // camera left with a label on the right empty space. Another one would be
  // camera on top with a caption beneath... Especially ones where you're
  // comparing one type of thing to another type of thing visually."*
  //
  // These are PROPOSALS for him to prune. Everything below renders in both
  // frames today; what is not settled is which of them the film language wants.

  {
    id: 'halves-with-a-statement',
    name: 'Halves, with a statement',
    role: 'inset',
    kind: 'Title card',
    description:
      'MATT’S 50/50, and the split turns with the frame: a vertical seam in '
      + '16:9 (shot left, words right), a horizontal one in 9:16 (shot top, '
      + 'words beneath). Even weighting is the argument — neither side is the '
      + 'subject, so the pair reads as a comparison rather than a caption.',
    content: [
      { id: 'statement', words: { min: 1, max: 5 }, max_chars: 40,
        description: 'What occupies the other half. Short — it is a half, not a page.' },
    ],
    variants: [
      { id: 'shot-first', name: 'Shot first', template: 'frame-split',
        slots: { statement: ['title'] }, axes: { camera_side: 'first', gutter: 0.012 },
        description: 'Shot takes the left/top half, the statement the other.' },
      { id: 'shot-second', name: 'Shot second', template: 'frame-split',
        slots: { statement: ['title'] }, axes: { camera_side: 'second', gutter: 0.012 },
        description: 'Mirrored — the statement leads and the shot answers it.' },
      { id: 'hard-seam', name: 'Hard seam', template: 'frame-split',
        slots: { statement: ['title'] }, axes: { camera_side: 'first', gutter: 0 },
        description: 'No gutter: the halves butt, which reads harder and more clinical.' },
    ],
  },
  {
    id: 'halves-with-a-caption',
    name: 'Halves, with a caption',
    role: 'inset',
    kind: 'B-roll card',
    description:
      'The same 50/50, for when the free half needs a sentence rather than a '
      + 'statement — a source, a date, a qualifying line under the heading.',
    content: [
      { id: 'statement', words: { min: 1, max: 5 }, max_chars: 40,
        description: 'The heading in the free half.' },
      { id: 'caption', words: { min: 2, max: 12 }, max_chars: 64,
        description: 'The line beneath it.' },
    ],
    variants: [
      { id: 'shot-first', name: 'Shot first', template: 'frame-split',
        slots: { statement: ['title'], caption: ['caption'] },
        axes: { camera_side: 'first', gutter: 0.012 }, description: 'Shot left/top.' },
      { id: 'shot-second', name: 'Shot second', template: 'frame-split',
        slots: { statement: ['title'], caption: ['caption'] },
        axes: { camera_side: 'second', gutter: 0.012 }, description: 'Shot right/bottom.' },
    ],
  },
  {
    id: 'shot-with-a-label',
    name: 'Shot with a label',
    role: 'inset',
    kind: 'Label',
    description:
      'MATT’S "camera left with a label on the right empty space" — and, in the '
      + 'vertical frame, his "camera on top with a caption beneath". One '
      + 'arrangement, two frames: the shot leads, a label keeps the remainder. '
      + 'The label STACKS its words in 16:9 (a narrow column) and RUNS them in '
      + '9:16 (a wide, short band), which is why this cannot be one layout scaled.',
    content: [
      { id: 'label', words: { min: 1, max: 3 }, max_chars: 28,
        description: 'One to three words. Longer belongs in `shot-with-a-caption`.' },
    ],
    variants: [
      { id: 'quiet', name: 'Quiet', template: 'frame-aside',
        slots: { label: ['label'] }, axes: { camera_share: 0.62, label_ink: 'dark' },
        description: 'The shot takes 62%; the label sits in dark ink.' },
      { id: 'emphatic', name: 'Emphatic', template: 'frame-aside',
        slots: { label: ['label'] }, axes: { camera_share: 0.55, label_ink: 'primary' },
        description: 'More room for the label, in the primary ink.' },
      { id: 'narrow', name: 'Narrow', template: 'frame-aside',
        slots: { label: ['label'] }, axes: { camera_share: 0.72, label_ink: 'secondary' },
        description: 'The shot nearly fills the frame; the label is a margin note.' },
    ],
  },
  {
    id: 'shot-with-a-caption',
    name: 'Shot with a caption',
    role: 'inset',
    kind: 'Lower third',
    description:
      'The shot leads and a full line explains it — the caption case of the '
      + 'same arrangement. A sentence, not a label: this is where a source, a '
      + 'date or a clause goes.',
    content: [
      { id: 'caption', words: { min: 3, max: 14 }, max_chars: 72,
        description:
          'A sentence or clause beside (16:9) or beneath (9:16) the shot. Capped '
          + 'at the GLOSS slot\'s 72, not the label slot\'s 28 — a caption is a '
          + 'line, and mapping it to the label slot is what a 35-character probe '
          + 'caught the first time this type was written.' },
    ],
    variants: [
      { id: 'quiet', name: 'Quiet', template: 'frame-aside',
        slots: { caption: ['gloss'] }, axes: { camera_share: 0.62, label_ink: 'dark' },
        description: 'The caption in the gloss slot, at reading weight.' },
      { id: 'roomy', name: 'Roomy', template: 'frame-aside',
        slots: { caption: ['gloss'] }, axes: { camera_share: 0.55, label_ink: 'dark' },
        description: 'More space for a longer line.' },
    ],
  },
  {
    id: 'shot-with-term-and-gloss',
    name: 'Shot with a term and its gloss',
    role: 'inset',
    kind: 'Label',
    description:
      'A named thing and what it means, beside the shot. Two supplied fields '
      + 'rather than one, which is what makes it a different TYPE from '
      + '`shot-with-a-label` and not a variant of it.',
    content: [
      { id: 'term', words: { min: 1, max: 3 }, max_chars: 24, description: 'The term.' },
      { id: 'gloss', words: { min: 2, max: 12 }, max_chars: 72, description: 'What it means.' },
    ],
    variants: [
      { id: 'quiet', name: 'Quiet', template: 'frame-aside',
        slots: { term: ['label'], gloss: ['gloss'] },
        axes: { camera_share: 0.6, label_ink: 'dark' }, description: 'Term over gloss.' },
      { id: 'emphatic', name: 'Emphatic', template: 'frame-aside',
        slots: { term: ['label'], gloss: ['gloss'] },
        axes: { camera_share: 0.55, label_ink: 'primary' }, description: 'The term in colour.' },
    ],
  },
  {
    id: 'before-and-after',
    name: 'Before and after',
    role: 'inset',
    kind: 'Diagram',
    description:
      'THE COMPARISON CASE Matt asked for. The shot is one state, a still is '
      + 'the other, and both are labelled — a comparison that does not say which '
      + 'is which is a layout, not an argument. Side by side in 16:9; STACKED in '
      + '9:16, because two things beside each other at 1080 across leaves 500 '
      + 'pixels apiece and neither reads.',
    content: [
      { id: 'before', words: { min: 1, max: 3 }, max_chars: 20, description: 'Label for the shot.' },
      { id: 'after', words: { min: 1, max: 3 }, max_chars: 20, description: 'Label for the compared still.' },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled', template: 'frame-compare',
        slots: { before: ['label-this'], after: ['label-that'] },
        axes: { divider: true, label_ink: 'primary' },
        description: 'A printed rule between the two.' },
      { id: 'open', name: 'Open', template: 'frame-compare',
        slots: { before: ['label-this'], after: ['label-that'] },
        axes: { divider: false, label_ink: 'dark' },
        description: 'No rule — the gap does the separating.' },
    ],
  },
  {
    id: 'this-against-that',
    name: 'This against that',
    role: 'inset',
    kind: 'Diagram',
    description:
      'The same pairing without the temporal claim: two things compared on any '
      + 'axis at all — a method against a method, a place against a place — with '
      + 'a statement over the pair naming what is being compared.',
    content: [
      { id: 'this', words: { min: 1, max: 3 }, max_chars: 20, description: 'Label for the shot.' },
      { id: 'that', words: { min: 1, max: 3 }, max_chars: 20, description: 'Label for the other.' },
      { id: 'question', words: { min: 1, max: 5 }, max_chars: 32,
        description: 'What is being compared, over the pair.' },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled', template: 'frame-compare',
        slots: { this: ['label-this'], that: ['label-that'], question: ['title'] },
        axes: { divider: true, label_ink: 'primary' }, description: 'With the rule.' },
      { id: 'open', name: 'Open', template: 'frame-compare',
        slots: { this: ['label-this'], that: ['label-that'], question: ['title'] },
        axes: { divider: false, label_ink: 'secondary' }, description: 'Without.' },
    ],
  },
  {
    id: 'then-and-now',
    name: 'Then and now',
    role: 'inset',
    kind: 'Diagram',
    description:
      'The temporal comparison, with dates rather than words as labels. Kept '
      + 'apart from `before-and-after` because what the writer supplies is '
      + 'different — a year is not a state — and the signature is the type.',
    content: [
      { id: 'then', words: { min: 1, max: 2 }, max_chars: 12, description: 'The earlier date.' },
      { id: 'now', words: { min: 1, max: 2 }, max_chars: 12, description: 'The later date.' },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled', template: 'frame-compare',
        slots: { then: ['label-this'], now: ['label-that'] },
        axes: { divider: true, label_ink: 'dark' }, description: 'Dated, with a rule.' },
      { id: 'open', name: 'Open', template: 'frame-compare',
        slots: { then: ['label-this'], now: ['label-that'] },
        axes: { divider: false, label_ink: 'primary' }, description: 'Dated, open.' },
    ],
  },
  {
    id: 'shot-over-evidence',
    name: 'Shot over evidence',
    role: 'inset',
    kind: 'B-roll card',
    description:
      'The inverse weighting, borrowed from lecture and commentary video: the '
      + 'material fills the frame and the shot sits small in a corner. Use when '
      + 'what is being shown matters more than who is showing it.',
    content: [
      { id: 'title', words: { min: 1, max: 5 }, max_chars: 36,
        description: 'What the evidence is.' },
      { id: 'source', words: { min: 1, max: 8 }, max_chars: 48,
        description: 'A small line under the shot — a source, a date.' },
    ],
    variants: [
      { id: 'bottom-right', name: 'Bottom right', template: 'frame-corner',
        slots: { title: ['title'], source: ['caption'] },
        axes: { corner: 'bottom-right', camera_scale: 0.26 },
        description: 'The default corner.' },
      { id: 'bottom-left', name: 'Bottom left', template: 'frame-corner',
        slots: { title: ['title'], source: ['caption'] },
        axes: { corner: 'bottom-left', camera_scale: 0.26 },
        description: 'When the evidence needs its right side.' },
      { id: 'top-right-small', name: 'Top right, small', template: 'frame-corner',
        slots: { title: ['title'], source: ['caption'] },
        axes: { corner: 'top-right', camera_scale: 0.2 },
        description: 'The smallest presence — a reminder rather than a presenter.' },
    ],
  },


  // -------------------------------------------------------------------------------
  // OVERLAY — see-through. The camera runs underneath and shows through.
  // Template pool: `lower-third` (text) and `nk-overlay` (no text) — the only
  // two that lay no ground.
  // -------------------------------------------------------------------------------

  {
    id: 'one-word-overlay',
    name: 'One-word overlay',
    role: 'overlay',
    kind: 'Label',
    description:
      'A single word named on the footage. The shortest thing the script can '
      + 'ask for, and the one that wants the most room per glyph.',
    content: [
      { id: 'word', words: { min: 1, max: 1 }, max_chars: 16,
        description: 'The word. One; a second one is a different type.' },
    ],
    variants: [
      { id: 'plate-tight', name: 'Tight plate', template: 'lower-third',
        slots: { word: ['title'] },
        axes: { band_width: 0.5, band_height: 0.16, band_ink: 'dark', edge_roughness: 0.35 },
        description: 'A short dark band, barely wider than the word.' },
      { id: 'plate-wide', name: 'Wide plate', template: 'lower-third',
        slots: { word: ['title'] },
        axes: { band_width: 0.68, band_height: 0.24, band_ink: 'primary', edge_roughness: 0.65 },
        description: 'The band runs long and rips hard — the word sits small in a big field.' },
      { id: 'plate-quiet', name: 'Quiet plate', template: 'lower-third',
        slots: { word: ['title'] },
        axes: { band_width: 0.54, band_height: 0.14, band_ink: 'tertiary', edge_roughness: 0.3 },
        description: 'Low, pale, almost clean-edged. For a word that should not interrupt.' },
    ],
  },

  {
    id: 'two-word-overlay',
    name: 'Two-word overlay',
    role: 'overlay',
    kind: 'Label',
    description:
      'MATT’S OWN EXAMPLE, and the reason word count is part of the signature. '
      + 'Two words, and the type’s job is to render both of them correctly in '
      + 'both frames — which for two of its three variants means one word per '
      + 'line rather than one line of two words.',
    content: [
      { id: 'words', words: { min: 2, max: 2 }, max_chars: 24,
        description: 'Exactly two words. Three is `phrase-overlay`.' },
    ],
    variants: [
      { id: 'stacked-plate', name: 'Stacked plate', template: 'lower-third',
        slots: { words: ['kicker', 'title'] },
        axes: { band_ink: 'dark', band_height: 0.22, band_width: 0.6 },
        description:
          'One word above the band, one on it. THE STACK: two text slots, one '
          + 'word each, not one slot holding both.' },
      { id: 'one-line-plate', name: 'One-line plate', template: 'lower-third',
        slots: { words: ['title'] },
        axes: { band_width: 0.62, band_ink: 'primary', band_height: 0.18 },
        description: 'Both words on the band, reading as one phrase.' },
      { id: 'stacked-emphatic', name: 'Emphatic stack', template: 'lower-third',
        slots: { words: ['kicker', 'title'] },
        axes: { band_ink: 'secondary', band_height: 0.24, edge_roughness: 0.7, band_width: 0.68 },
        description: 'The stacked split at full height, with the band torn hard.' },
    ],
  },

  {
    id: 'phrase-overlay',
    name: 'Phrase overlay',
    role: 'overlay',
    kind: 'Label',
    description:
      'Three to six words over the footage — long enough to be a statement, '
      + 'short enough to stay furniture.',
    content: [
      { id: 'phrase', words: { min: 3, max: 6 }, max_chars: 34,
        description: 'The phrase. Longer than this is a pull-quote, not a label.' },
    ],
    variants: [
      { id: 'plate-run', name: 'Running plate', template: 'lower-third',
        slots: { phrase: ['title'] },
        axes: { band_width: 0.68, band_height: 0.2, band_ink: 'dark' },
        description: 'One long line on a long band.' },
      { id: 'plate-break', name: 'Broken plate', template: 'lower-third',
        slots: { phrase: ['kicker', 'title'] },
        axes: { band_width: 0.62, band_height: 0.2, band_ink: 'primary' },
        description: 'The phrase breaks across two lines — the front half above the band.' },
      { id: 'plate-break-tall', name: 'Tall break', template: 'lower-third',
        slots: { phrase: ['kicker', 'title'] },
        axes: { band_height: 0.24, band_ink: 'dark', band_width: 0.68, edge_roughness: 0.6 },
        description: 'The same break at full band height, for the line that has to land.' },
    ],
  },

  {
    id: 'term-and-gloss-overlay',
    name: 'Term and gloss overlay',
    role: 'overlay',
    kind: 'Label',
    description:
      'A term named and then explained in a breath. Two supplied lines, which '
      + 'is what makes it a different type from `phrase-overlay` rather than a '
      + 'look of it.',
    content: [
      { id: 'term', words: { min: 1, max: 3 }, max_chars: 22,
        description: 'The thing being named.' },
      { id: 'gloss', words: { min: 1, max: 7 }, max_chars: 34,
        description: 'What it means, in one breath.' },
    ],
    variants: [
      { id: 'gloss-above', name: 'Gloss above', template: 'lower-third',
        slots: { term: ['title'], gloss: ['kicker'] },
        axes: { band_ink: 'dark', band_height: 0.18, band_width: 0.6 },
        description: 'Term on the band, gloss floating above it.' },
      { id: 'gloss-above-wide', name: 'Wide gloss', template: 'lower-third',
        slots: { term: ['title'], gloss: ['kicker'] },
        axes: { band_width: 0.68, band_height: 0.22, band_ink: 'primary' },
        description: 'The same pair with room for a longer gloss.' },
      { id: 'gloss-above-quiet', name: 'Quiet gloss', template: 'lower-third',
        slots: { term: ['title'], gloss: ['kicker'] },
        axes: { band_ink: 'tertiary', band_height: 0.15, edge_roughness: 0.3, band_width: 0.54 },
        description: 'Pale and low — a definition that does not want the shot.' },
    ],
  },

  {
    id: 'lower-third-name',
    name: 'Lower third — name',
    role: 'overlay',
    kind: 'Lower third',
    description: 'Who this is. One line, and nothing under it.',
    content: [
      { id: 'name', words: { min: 1, max: 4 }, max_chars: 34,
        description: 'The person’s name as it should read on screen.' },
    ],
    variants: [
      { id: 'plate-dark', name: 'Dark plate', template: 'lower-third',
        slots: { name: ['title'] },
        axes: { band_ink: 'dark', band_height: 0.18, band_width: 0.58, edge_roughness: 0.5 },
        description: 'The house name-plate.' },
      { id: 'plate-colour', name: 'Colour plate', template: 'lower-third',
        slots: { name: ['title'] },
        axes: { band_ink: 'primary', band_height: 0.2, band_width: 0.62, edge_roughness: 0.6 },
        description: 'The same plate in the film’s primary ink.' },
      { id: 'plate-slim', name: 'Slim plate', template: 'lower-third',
        slots: { name: ['title'] },
        axes: { band_ink: 'secondary', band_height: 0.14, band_width: 0.5, edge_roughness: 0.35 },
        description: 'A thin strip for a busy frame.' },
    ],
  },

  {
    id: 'lower-third-name-and-role',
    name: 'Lower third — name and role',
    role: 'overlay',
    kind: 'Lower third',
    description:
      'Who this is and why they are talking. The second supplied line is what '
      + 'separates it from `lower-third-name`.',
    content: [
      { id: 'name', words: { min: 1, max: 4 }, max_chars: 34,
        description: 'The person’s name.' },
      { id: 'role', words: { min: 1, max: 6 }, max_chars: 34,
        description: 'Their title, affiliation, or what they did.' },
    ],
    variants: [
      { id: 'role-above', name: 'Role above', template: 'lower-third',
        slots: { name: ['title'], role: ['kicker'] },
        axes: { band_ink: 'dark', band_height: 0.18, band_width: 0.6 },
        description: 'Name on the band, role in small type above it.' },
      { id: 'role-above-wide', name: 'Wide role', template: 'lower-third',
        slots: { name: ['title'], role: ['kicker'] },
        axes: { band_ink: 'primary', band_height: 0.22, band_width: 0.68, edge_roughness: 0.65 },
        description: 'For a long affiliation that needs the width.' },
      { id: 'role-above-slim', name: 'Slim role', template: 'lower-third',
        slots: { name: ['title'], role: ['kicker'] },
        axes: { band_ink: 'tertiary', band_height: 0.15, band_width: 0.52, edge_roughness: 0.3 },
        description: 'The quiet version, for a frame that is already full.' },
    ],
  },

  {
    id: 'step-callout-overlay',
    name: 'Step callout overlay',
    role: 'overlay',
    kind: 'Step callout',
    description:
      'A numbered step named over the footage while the step is happening. The '
      + 'number is supplied separately from the label, which is the signature.',
    content: [
      { id: 'step', words: { min: 1, max: 1 }, max_chars: 3,
        description: 'The step marker — "1", "2.", "iii".' },
      { id: 'label', words: { min: 1, max: 6 }, max_chars: 34,
        description: 'What happens at this step.' },
    ],
    variants: [
      { id: 'number-above', name: 'Number above', template: 'lower-third',
        slots: { step: ['kicker'], label: ['title'] },
        axes: { band_ink: 'dark', band_height: 0.18, band_width: 0.6 },
        description: 'Step number in small type, the action on the band.' },
      { id: 'number-above-wide', name: 'Wide step', template: 'lower-third',
        slots: { step: ['kicker'], label: ['title'] },
        axes: { band_ink: 'primary', band_height: 0.22, band_width: 0.68 },
        description: 'Room for a longer action line.' },
      { id: 'number-above-quiet', name: 'Quiet step', template: 'lower-third',
        slots: { step: ['kicker'], label: ['title'] },
        axes: { band_ink: 'tertiary', band_height: 0.14, band_width: 0.5, edge_roughness: 0.3 },
        description: 'For a sequence of steps that should not keep shouting.' },
    ],
  },

  {
    id: 'frame-overlay',
    name: 'Frame overlay',
    role: 'overlay',
    kind: 'Collage',
    description:
      'Collage furniture around the edges with the middle left clear — the '
      + 'footage is dressed, not covered. Carries no language, so the script '
      + 'names it and supplies nothing.',
    content: [],
    variants: [
      { id: 'corners-both', name: 'Both corners', template: 'nk-overlay',
        slots: {},
        axes: { corner: 'both', density: 2, accent_count: 12, porthole: true },
        description: 'The house frame: both corners dressed, porthole on.' },
      { id: 'corners-left', name: 'Left corner, dense', template: 'nk-overlay',
        slots: {},
        axes: { corner: 'left', density: 3, accent_count: 20, porthole: true },
        description:
          'Weight to one side, for a subject sitting right. THE PORTHOLE STAYS: it\n           is this template\u2019s living window, and the three-tells guarantee refuses a\n           paper-toss composition without one. This variant set it false and could\n           not compose through the lane at all \u2014 found by rendering the contact\n           sheet, because the catalogue check stopped before the gate.' },
      { id: 'corners-right-sparse', name: 'Right corner, sparse', template: 'nk-overlay',
        slots: {},
        axes: { corner: 'right', density: 1, accent_count: 6, porthole: true },
        description:
          'The lightest touch the frame does \u2014 one sparse corner. The porthole\n           stays for the same reason as `corners-left`: it is the living window.' },
    ],
  },

  // -------------------------------------------------------------------------------
  // CUTAWAY — full-frame. A solid ground covers the camera; it keeps running
  // underneath (§5d: a cutaway sits ABOVE the camera, it does not replace it).
  // -------------------------------------------------------------------------------

  {
    id: 'title-card-cutaway',
    name: 'Title card',
    role: 'cutaway',
    kind: 'Title card',
    description: 'The opener or act break. One statement, full frame.',
    content: [
      { id: 'title', words: { min: 1, max: 4 }, max_chars: 24,
        description: 'The card’s one statement. 24 chars is the `banded-print` ceiling, '
          + 'and a type may not accept what one of its own variants cannot render.' },
    ],
    variants: [
      { id: 'brush', name: 'Brush', template: 'title-card',
        slots: { title: ['title'] },
        axes: { title_treatment: 'brush', rotation_energy: 0.45, density: 2, hero_scale: 0.28 },
        description: 'The hand-made opener — brush glyphs stamping in.' },
      { id: 'ransom', name: 'Ransom', template: 'title-card',
        slots: { title: ['title'] },
        axes: { title_treatment: 'procedural-cutout', rotation_energy: 0.7, density: 3, window_shape: 'arch' },
        description: 'Cut-out letters, tossed on the table.' },
      { id: 'bleed', name: 'Bleed', template: 'title-card',
        slots: { title: ['title'] },
        axes: { title_treatment: 'bleed', rotation_energy: 0.2, hero_scale: 0.36, window_shape: 'circle' },
        description: 'Oversized and cropped by the frame.' },
      { id: 'print-band', name: 'Print band', template: 'banded-print',
        slots: { title: ['title'] },
        axes: { band_count: 5, ink_spread: 0.8, glyph_scale: 0.65 },
        description: 'The schoolbook cover: the title in a reserved band. A '
          + 'different template, not a different treatment.' },
    ],
  },

  {
    id: 'kicker-and-title-cutaway',
    name: 'Kicker and title card',
    role: 'cutaway',
    kind: 'Title card',
    description:
      'A card that says where you are as well as what this is. Two supplied '
      + 'lines — a chapter label and the statement under it.',
    content: [
      { id: 'kicker', words: { min: 1, max: 4 }, max_chars: 24,
        description: 'The quiet label — "CHAPTER ONE", "1929".' },
      { id: 'title', words: { min: 1, max: 5 }, max_chars: 28,
        description: 'The statement it introduces.' },
    ],
    variants: [
      { id: 'card-brush', name: 'Brush card', template: 'title-card',
        slots: { kicker: ['kicker'], title: ['title'] },
        axes: { title_treatment: 'brush', rotation_energy: 0.4, density: 2 },
        description: 'The house pair — kicker above a brushed title.' },
      { id: 'card-plain', name: 'Plain card', template: 'title-card',
        slots: { kicker: ['kicker'], title: ['title'] },
        axes: { title_treatment: 'plain', rotation_energy: 0.15, density: 1, hero_scale: 0.22 },
        description: 'System, not hand — for a card that is information.' },
      { id: 'card-stacked', name: 'Stacked card', template: 'title-card',
        slots: { kicker: ['kicker'], title: ['title'] },
        axes: { title_treatment: 'stacked', rotation_energy: 0.55, density: 3, window_shape: 'arch' },
        description: 'The Habitat pair — weights stacked, energy up.' },
      { id: 'sheet', name: 'Specimen sheet', template: 'specimen',
        slots: { kicker: ['kicker'], title: ['line'] },
        axes: { field_ink: 'dark', window_shape: 'circle' },
        description: 'The type-sheet reading of the same two lines.' },
    ],
  },

  {
    id: 'pull-quote-cutaway',
    name: 'Pull-quote card',
    role: 'cutaway',
    kind: 'Pull-quote',
    description:
      'Somebody’s words, full frame, with the attribution that makes them '
      + 'somebody’s. The attribution is what separates this from a title card.',
    content: [
      { id: 'quote', words: { min: 3, max: 8 }, max_chars: 28,
        description: 'The line being quoted. 28 chars is `title-card`’s ceiling.' },
      { id: 'attribution', words: { min: 1, max: 5 }, max_chars: 24,
        description: 'Who said it. 24 is `specimen`’s kicker ceiling.' },
    ],
    variants: [
      { id: 'specimen-sheet', name: 'Specimen sheet', template: 'specimen',
        slots: { quote: ['line'], attribution: ['kicker'] },
        axes: { field_ink: 'dark', window_shape: 'circle' },
        description: 'The quote as a line of set type on a dark field.' },
      { id: 'specimen-arch', name: 'Arched specimen', template: 'specimen',
        slots: { quote: ['line'], attribution: ['kicker'] },
        axes: { field_ink: 'primary', window_shape: 'arch' },
        description: 'The same sheet, warmer field and an arched window.' },
      { id: 'card-stamped', name: 'Stamped card', template: 'title-card',
        slots: { quote: ['title'], attribution: ['kicker'] },
        axes: { title_treatment: 'plain', rotation_energy: 0.2, density: 1 },
        description: 'The quote stamped as a card statement, credit above.' },
      { id: 'card-bleed', name: 'Bleeding quote', template: 'title-card',
        slots: { quote: ['title'], attribution: ['kicker'] },
        axes: { title_treatment: 'bleed', rotation_energy: 0.35, hero_scale: 0.34 },
        description: 'Oversized and cropped — for the line the film is named after.' },
    ],
  },

  {
    id: 'step-callout-cutaway',
    name: 'Step card',
    role: 'cutaway',
    kind: 'Step callout',
    description:
      'A numbered step given the whole frame — the beat where the film stops '
      + 'and counts.',
    content: [
      { id: 'step', words: { min: 1, max: 1 }, max_chars: 2,
        description: 'The step marker. Two characters, because `banded-print`’s '
          + 'glyph slot is an oversized single mark.' },
      { id: 'label', words: { min: 1, max: 6 }, max_chars: 24,
        description: 'What this step is.' },
    ],
    variants: [
      { id: 'glyph-band', name: 'Glyph band', template: 'banded-print',
        slots: { step: ['glyph'], label: ['title'] },
        axes: { band_count: 4, glyph_scale: 0.7, ink_spread: 0.7 },
        description: 'The number as the oversized bleeding glyph, the step in a band.' },
      { id: 'glyph-band-tall', name: 'Tall glyph band', template: 'banded-print',
        slots: { step: ['glyph'], label: ['title'] },
        axes: { band_count: 6, glyph_scale: 0.75, ink_spread: 1 },
        description: 'More bands, bigger number, ink pushed all the way out.' },
      { id: 'glyph-band-open', name: 'Open glyph band', template: 'banded-print',
        slots: { step: ['glyph'], label: ['title'] },
        axes: { band_count: 3, glyph_scale: 0.5, ink_spread: 0.2 },
        description: 'Three bands and a restrained glyph — air around the step.' },
      { id: 'sheet-step', name: 'Specimen step', template: 'specimen',
        slots: { step: ['kicker'], label: ['line'] },
        axes: { field_ink: 'tertiary', window_shape: 'semicircle' },
        description: 'The step as a numbered specimen rather than a poster.' },
    ],
  },

  {
    id: 'diagram-cutaway',
    name: 'Diagram card',
    role: 'cutaway',
    kind: 'Diagram',
    description:
      'A drawn relationship between two named things — the map sheet with its '
      + 'scribed route. Three supplied lines: what this shows, and the two ends.'
      + ' There is no see-through counterpart: no template renders a labelled '
      + 'figure over footage, and a type that reached no layout would be the '
      + 'gap this catalogue exists to close.',
    content: [
      { id: 'title', words: { min: 1, max: 6 }, max_chars: 36,
        description: 'What the diagram shows.' },
      { id: 'label_a', words: { min: 1, max: 3 }, max_chars: 18,
        description: 'The first named end of the route.' },
      { id: 'label_b', words: { min: 1, max: 3 }, max_chars: 18,
        description: 'The second.' },
    ],
    variants: [
      { id: 'route-three', name: 'Three legs', template: 'map',
        slots: { title: ['title'], label_a: ['label-a'], label_b: ['label-b'] },
        axes: { route_legs: 3, route_ink: 'dark', camera_move: 'push', camera_amount: 0.4, markers: true },
        description: 'The house diagram — a stepped three-leg route, pushing in.' },
      { id: 'route-one', name: 'Direct', template: 'map',
        slots: { title: ['title'], label_a: ['label-a'], label_b: ['label-b'] },
        axes: { route_legs: 1, route_ink: 'primary', camera_move: 'pan', camera_amount: 0.6, markers: true },
        description: 'One straight leg — for a relationship with no detour in it.' },
      { id: 'route-four-quiet', name: 'Four legs, quiet', template: 'map',
        slots: { title: ['title'], label_a: ['label-a'], label_b: ['label-b'] },
        axes: { route_legs: 4, route_ink: 'tertiary', camera_move: 'pull', camera_amount: 0.7, markers: false },
        description: 'A long wandering route, markers off, pulling back.' },
    ],
  },

  {
    id: 'b-roll-card-cutaway',
    name: 'B-roll card',
    role: 'cutaway',
    kind: 'B-roll card',
    description:
      'An archival still given depth and a caption — the film cuts away to a '
      + 'picture and tells you what it is.',
    content: [
      { id: 'caption', words: { min: 1, max: 7 }, max_chars: 48,
        description: 'What this is and when — "GRAND CENTRAL, 1929".' },
    ],
    variants: [
      { id: 'push-shallow', name: 'Push, shallow', template: 'archival-parallax',
        slots: { caption: ['caption'] },
        axes: { camera_move: 'push', camera_amount: 0.35, defocus: 0.2, subject_scale: 0.42 },
        description: 'A slow push with most of the depth in focus.' },
      { id: 'pan-deep', name: 'Pan, deep', template: 'archival-parallax',
        slots: { caption: ['caption'] },
        axes: { camera_move: 'pan', camera_amount: 0.7, defocus: 0.6, subject_scale: 0.5, subject_depth: 0.95 },
        description: 'A wide pan with the background falling away.' },
      { id: 'pull-soft', name: 'Pull, soft', template: 'archival-parallax',
        slots: { caption: ['caption'] },
        axes: { camera_move: 'pull', camera_amount: 0.5, defocus: 0.8, subject_depth: 1, mid_depth: 0.6 },
        description: 'Pulling back out of a very soft field.' },
    ],
  },

  {
    id: 'collage-cutaway',
    name: 'Collage field',
    role: 'cutaway',
    kind: 'Collage',
    description:
      'A full-frame composed field with no language in it — the shot that '
      + 'carries mood or place rather than a statement. Element-driven, so the '
      + 'script names it and supplies no text.',
    content: [],
    variants: [
      { id: 'night-field', name: 'Night field', template: 'nk-backdrop',
        slots: {},
        axes: { star_count: 80, hill_count: 3, sky_angle: 18, moon: true },
        description: 'The house night sky — stars, hills, moon up.' },
      { id: 'dawn-field', name: 'Dawn field', template: 'nk-backdrop',
        slots: {},
        axes: { star_count: 24, hill_count: 2, sky_angle: 36, moon: true },
        description:
          'Few stars, a steep gradient. The moon stays: it is this template\u2019s\n           living window, and switching it off left a composition the lane\n           refuses.' },
      { id: 'archival-field', name: 'Archival field', template: 'archival-parallax',
        slots: {},
        axes: { camera_move: 'pull', camera_amount: 0.5, defocus: 0.5, subject_scale: 0.5 },
        description: 'The separated-stills space with the caption dropped — '
          + 'picture only.' },
    ],
  },

  // =====================================================================================
  // STACKED LINES (Order 3.9996) — the shape Matt named that no template rendered
  // =====================================================================================
  //
  // Matt, 2026-08-08: *"We have templates that are single-word, a single line,
  // or two or three lines... Is it a graph? Is it bullet points vertically
  // stacked? Those need to match up with the type of text."* Word count was
  // already signature; LINE count was not, so "two or three lines" could not be
  // named by a script. `lines` on ShotContentSlot is that axis, and these are
  // the types it makes reachable.
  //
  // THEY FAN OUT BY EXACT COUNT, exactly as the word types do, and for the same
  // reason: a variant's `slots` map is static, so the row count is fixed at the
  // variant. A type declaring 2-5 lines would have variants that silently
  // dropped the fourth line, which is the failure mode `distributeLines` refuses
  // outright.
  //
  // A STATEMENT AND A LIST ARE ONE TYPE HERE, TWO VARIANTS — and this is the
  // file's own rule applied honestly, not a shortcut. "Two shapes are the SAME
  // type iff a writer could hand the same content to both without editing it."
  // Three lines are three lines; whether they read as three bullets or as one
  // sentence broken across rows is a RULE UNDER EACH ROW, which is a look. If
  // Matt wants them apart, that is a design call and it splits cleanly — it is
  // on the 3.9996 review list.

  // ---------------------------------------------------------------------------
  // TWO-LINE-CUTAWAY — Order 3.9996. Two lines — a statement broken, or two points.
  // ---------------------------------------------------------------------------
  {
    id: 'two-line-cutaway',
    name: '2 lines, stacked',
    role: 'cutaway',
    // The closest surviving kind in the DB's own vocabulary. A stacked list is
    // an enumeration, which is what `Step callout` names there; the alternative
    // was inventing a tenth kind, and OVERLAY_KINDS is kept verbatim precisely
    // so a type can be traced back to the vocabulary it came from. Flagged for
    // Matt in the 3.9996 review rather than settled here.
    kind: 'Step callout',
    description:
      'Exactly 2 lines the writer supplies and means to keep separate, stacked '
      + 'one per row. Two lines — a statement broken, or two points.',
    content: [
      {
        id: 'points',
        words: { min: 1, max: 6 },
        lines: { min: 2, max: 2 },
        max_chars: 34,
        description:
          '2 lines, newline-separated. Word and character bounds apply PER LINE '
          + '— a 2-item list is 2 short items, not one budget shared between them.',
      },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled list', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push' },
        description: 'Each point on its own row with a cut rule beneath it — the list reading.' },
      { id: 'unruled', name: 'Plain stack', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2'] },
        axes: { rules: false, field_ink: 'paper', camera_move: 'pull' },
        description: 'The same 2 lines with no rules — one statement broken across rows rather than 2 items.' },
      { id: 'tinted', name: 'Tinted field', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan' },
        description: 'Ruled, on a tinted card field — for a stack that has to hold against busy footage.' },
      { id: 'numbered', name: 'Numbered steps', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push', camera_amount: 0.2 },
        description:
          'The steps shown ALL AT ONCE rather than called out one at a time — the '
          + 'writer supplies the numbers in the lines ("1. Open the valve"). '
          + '`step-callout` is the other reading: one step, while it happens.' },
      { id: 'paired', name: 'Label and value rows', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan', camera_amount: 0.2 },
        description:
          'Each row a pair the writer sets — "Depth — 40 m", "1969 — Apollo". This '
          + 'is how a spec block and a dated timeline reach a layout: they are N '
          + 'lines, so they are this type, and the pairing is the LOOK.' },
    ],
  },

  // ---------------------------------------------------------------------------
  // THREE-LINE-CUTAWAY — Order 3.9996. Three lines — the count Matt named, and the one a viewer holds.
  // ---------------------------------------------------------------------------
  {
    id: 'three-line-cutaway',
    name: '3 lines, stacked',
    role: 'cutaway',
    // The closest surviving kind in the DB's own vocabulary. A stacked list is
    // an enumeration, which is what `Step callout` names there; the alternative
    // was inventing a tenth kind, and OVERLAY_KINDS is kept verbatim precisely
    // so a type can be traced back to the vocabulary it came from. Flagged for
    // Matt in the 3.9996 review rather than settled here.
    kind: 'Step callout',
    description:
      'Exactly 3 lines the writer supplies and means to keep separate, stacked '
      + 'one per row. Three lines — the count Matt named, and the one a viewer holds.',
    content: [
      {
        id: 'points',
        words: { min: 1, max: 6 },
        lines: { min: 3, max: 3 },
        max_chars: 34,
        description:
          '3 lines, newline-separated. Word and character bounds apply PER LINE '
          + '— a 3-item list is 3 short items, not one budget shared between them.',
      },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled list', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push' },
        description: 'Each point on its own row with a cut rule beneath it — the list reading.' },
      { id: 'unruled', name: 'Plain stack', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: false, field_ink: 'paper', camera_move: 'pull' },
        description: 'The same 3 lines with no rules — one statement broken across rows rather than 3 items.' },
      { id: 'tinted', name: 'Tinted field', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan' },
        description: 'Ruled, on a tinted card field — for a stack that has to hold against busy footage.' },
      { id: 'numbered', name: 'Numbered steps', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push', camera_amount: 0.2 },
        description:
          'The steps shown ALL AT ONCE rather than called out one at a time — the '
          + 'writer supplies the numbers in the lines ("1. Open the valve"). '
          + '`step-callout` is the other reading: one step, while it happens.' },
      { id: 'paired', name: 'Label and value rows', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan', camera_amount: 0.2 },
        description:
          'Each row a pair the writer sets — "Depth — 40 m", "1969 — Apollo". This '
          + 'is how a spec block and a dated timeline reach a layout: they are N '
          + 'lines, so they are this type, and the pairing is the LOOK.' },
    ],
  },

  // ---------------------------------------------------------------------------
  // FIVE-LINE-CUTAWAY — Order 3.9996. Five lines — the ceiling, past which the type shrinks faster than the reader gains.
  // ---------------------------------------------------------------------------
  {
    id: 'five-line-cutaway',
    name: '5 lines, stacked',
    role: 'cutaway',
    // The closest surviving kind in the DB's own vocabulary. A stacked list is
    // an enumeration, which is what `Step callout` names there; the alternative
    // was inventing a tenth kind, and OVERLAY_KINDS is kept verbatim precisely
    // so a type can be traced back to the vocabulary it came from. Flagged for
    // Matt in the 3.9996 review rather than settled here.
    kind: 'Step callout',
    description:
      'Exactly 5 lines the writer supplies and means to keep separate, stacked '
      + 'one per row. Five lines — the ceiling, past which the type shrinks faster than the reader gains.',
    content: [
      {
        id: 'points',
        words: { min: 1, max: 5 },
        lines: { min: 5, max: 5 },
        max_chars: 30,
        description:
          '5 lines, newline-separated. Word and character bounds apply PER LINE '
          + '— a 5-item list is 5 short items, not one budget shared between them.',
      },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled list', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4', 'item-5'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push' },
        description: 'Each point on its own row with a cut rule beneath it — the list reading.' },
      { id: 'unruled', name: 'Plain stack', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4', 'item-5'] },
        axes: { rules: false, field_ink: 'paper', camera_move: 'pull' },
        description: 'The same 5 lines with no rules — one statement broken across rows rather than 5 items.' },
      { id: 'tinted', name: 'Tinted field', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4', 'item-5'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan' },
        description: 'Ruled, on a tinted card field — for a stack that has to hold against busy footage.' },
      { id: 'numbered', name: 'Numbered steps', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4', 'item-5'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push', camera_amount: 0.2 },
        description:
          'The steps shown ALL AT ONCE rather than called out one at a time — the '
          + 'writer supplies the numbers in the lines ("1. Open the valve"). '
          + '`step-callout` is the other reading: one step, while it happens.' },
      { id: 'paired', name: 'Label and value rows', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4', 'item-5'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan', camera_amount: 0.2 },
        description:
          'Each row a pair the writer sets — "Depth — 40 m", "1969 — Apollo". This '
          + 'is how a spec block and a dated timeline reach a layout: they are N '
          + 'lines, so they are this type, and the pairing is the LOOK.' },
    ],
  },

  // =====================================================================================
  // BREADTH PASS (Order 3.9997) — a type for every situation a script produces
  // =====================================================================================
  //
  // Matt, 2026-08-09: *"determine those different variations on different
  // situations and build them into the compositor so that we have a full library
  // of elements and templates for each situation. Ideally, multiple templates for
  // each situation."* Spec §5g `types.coverage-before-polish`: a rough
  // arrangement carrying the right content beats a beautiful one that leaves a
  // situation unreachable, and the LOOK is authored by hand in the Compositor
  // (`types.arrangement-is-authored-in-the-tool`), not by tuning regions here.
  //
  // FOUR OF THE SITUATIONS IN §5g'S TABLE ARE NOT HERE, and their absence is the
  // grain rule working rather than a gap:
  //   · "a question put to the viewer" is one line of 5-9 words — the same
  //     signature as a statement. A writer hands the same content to both without
  //     editing it, so it is `sentence-cutaway` with a question mark in it.
  //   · "key-value spec block" and "a dated timeline" are N lines of
  //     "Label — value" and "1969 — Apollo". Same signature as a list. They are
  //     the `*-line-cutaway` family, and the `paired` variants below set them.
  //   · "translation pair" and "how to say it" are a short term and a longer
  //     gloss — exactly `term-and-gloss-overlay`, which already exists.
  // Declaring separate types for these would be four names for one shape, which
  // is what the type/variant split exists to prevent.

  {
    id: 'sentence-cutaway',
    name: 'One sentence',
    role: 'cutaway',
    kind: 'Pull-quote',
    description:
      'A whole sentence, full frame, in the film\'s own voice — no attribution, '
      + 'which is what separates it from a pull-quote. A question is this type.',
    content: [
      { id: 'sentence', words: { min: 5, max: 9 }, max_chars: 44,
        description: 'One line. 44 chars is `specimen`\'s ceiling, the narrowest slot any variant maps.' },
    ],
    variants: [
      { id: 'set-dark', name: 'Set on dark', template: 'specimen',
        slots: { sentence: ['line'] }, axes: { field_ink: 'dark', window_shape: 'circle' },
        description: 'The line set as specimen type on a dark field.' },
      { id: 'set-warm', name: 'Set on warm', template: 'specimen',
        slots: { sentence: ['line'] }, axes: { field_ink: 'primary', window_shape: 'arch' },
        description: 'The same line, warmer field, arched window.' },
      { id: 'over-plate', name: 'Over an archival plate', template: 'archival-parallax',
        slots: { sentence: ['caption'] }, axes: { camera_move: 'push', camera_amount: 0.35 },
        description: 'The sentence carried on the caption strip over a photo plate.' },
    ],
  },

  {
    id: 'four-line-cutaway',
    name: '4 lines, stacked',
    role: 'cutaway',
    kind: 'Step callout',
    description:
      'Exactly four lines the writer supplies and means to keep separate. Completes '
      + 'the 2/3/4/5 run so a script can name any of them.',
    content: [
      { id: 'points', words: { min: 1, max: 6 }, lines: { min: 4, max: 4 }, max_chars: 34,
        description: '4 lines, newline-separated. Word and char bounds apply PER LINE.' },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled list', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push' },
        description: 'Each point on its own row with a cut rule beneath it.' },
      { id: 'unruled', name: 'Plain stack', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4'] },
        axes: { rules: false, field_ink: 'paper', camera_move: 'pull' },
        description: 'No rules — one statement broken across rows rather than four items.' },
      { id: 'tinted', name: 'Tinted field', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan' },
        description: 'Ruled on a tinted card — for a stack that must hold against busy footage.' },
      { id: 'numbered', name: 'Numbered steps', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push', camera_amount: 0.2 },
        description:
          'The steps shown ALL AT ONCE rather than called out one at a time — the '
          + 'writer supplies the numbers in the lines ("1. Open the valve"). '
          + '`step-callout` is the other reading: one step, while it happens.' },
      { id: 'paired', name: 'Label and value rows', template: 'bullet-card',
        slots: { points: ['item-1', 'item-2', 'item-3', 'item-4'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan', camera_amount: 0.2 },
        description:
          'Each row a pair the writer sets — "Depth — 40 m", "1969 — Apollo". This '
          + 'is how a spec block and a dated timeline reach a layout: they are N '
          + 'lines, so they are this type, and the pairing is the LOOK.' },
    ],
  },

  {
    id: 'headed-list-cutaway',
    name: 'A list with a heading',
    role: 'cutaway',
    kind: 'Step callout',
    description:
      'Three points under a heading that says what the list is FOR. The heading is '
      + 'what a writer must supply beyond the points, so it is a different type '
      + 'rather than a variant — and it is the only type that fills `bullet-card`\'s '
      + 'kicker, which nothing else reaches.',
    content: [
      { id: 'heading', words: { min: 1, max: 4 }, max_chars: 28,
        description: 'What the list is about. 28 is `bullet-card`\'s kicker ceiling.' },
      { id: 'points', words: { min: 1, max: 6 }, lines: { min: 3, max: 3 }, max_chars: 34,
        description: '3 lines, one per row.' },
    ],
    variants: [
      { id: 'ruled', name: 'Ruled under a heading', template: 'bullet-card',
        slots: { heading: ['kicker'], points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push' },
        description: 'Heading above, three ruled rows beneath.' },
      { id: 'unruled', name: 'Plain under a heading', template: 'bullet-card',
        slots: { heading: ['kicker'], points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: false, field_ink: 'paper', camera_move: 'pull' },
        description: 'Heading above, three unruled rows.' },
      { id: 'tinted', name: 'Tinted, headed', template: 'bullet-card',
        slots: { heading: ['kicker'], points: ['item-1', 'item-2', 'item-3'] },
        axes: { rules: true, field_ink: 'tertiary', camera_move: 'pan' },
        description: 'Headed and ruled on a tinted field.' },
    ],
  },

  {
    id: 'figure-and-label-cutaway',
    name: 'A number and what it measures',
    role: 'cutaway',
    kind: 'Diagram',
    description:
      'A statistic, full frame, with the phrase that says what it counts. The number '
      + 'without the label is a title card; the label is what makes it a figure.',
    content: [
      { id: 'figure', words: { min: 1, max: 1 }, max_chars: 8,
        description: 'The number itself — "40%", "1,200", "3x".' },
      { id: 'label', words: { min: 2, max: 6 }, max_chars: 32,
        description: 'What it measures. 32 is `title-card`\'s kicker ceiling.' },
    ],
    variants: [
      { id: 'card-stamped', name: 'Stamped card', template: 'title-card',
        slots: { figure: ['title'], label: ['kicker'] },
        axes: { title_treatment: 'plain', rotation_energy: 0.2, density: 1 },
        description: 'The figure stamped large, its label above.' },
      { id: 'card-bleed', name: 'Bleeding figure', template: 'title-card',
        slots: { figure: ['title'], label: ['kicker'] },
        axes: { title_treatment: 'bleed', rotation_energy: 0.35, hero_scale: 0.34 },
        description: 'Oversized and cropped — for the number the film turns on.' },
      { id: 'specimen-set', name: 'Set on a field', template: 'specimen',
        slots: { figure: ['kicker'], label: ['line'] },
        axes: { field_ink: 'dark', window_shape: 'circle' },
        description: 'Figure as the kicker, label set as the specimen line.' },
    ],
  },

  {
    id: 'figure-label-and-source-cutaway',
    name: 'A number, its label, and its source',
    role: 'cutaway',
    kind: 'Diagram',
    description:
      'The figure card that has to be citable. The source line is what a writer must '
      + 'supply beyond the figure and its label, so it is its own type.',
    content: [
      { id: 'figure', words: { min: 1, max: 1 }, max_chars: 8, description: 'The number.' },
      { id: 'label', words: { min: 2, max: 6 }, max_chars: 34, description: 'What it measures.' },
      { id: 'source', words: { min: 1, max: 6 }, max_chars: 34, description: 'Where it comes from.' },
    ],
    variants: [
      { id: 'stacked-ruled', name: 'Ruled stack', template: 'bullet-card',
        slots: { figure: ['kicker'], label: ['item-1'], source: ['item-2'] },
        axes: { rules: true, field_ink: 'paper', camera_move: 'push' },
        description: 'Figure above, label and source as two ruled rows.' },
      { id: 'stacked-plain', name: 'Plain stack', template: 'bullet-card',
        slots: { figure: ['kicker'], label: ['item-1'], source: ['item-2'] },
        axes: { rules: false, field_ink: 'paper', camera_move: 'pull' },
        description: 'The same three parts with no rules.' },
      { id: 'stacked-tinted', name: 'Tinted stack', template: 'bullet-card',
        slots: { figure: ['kicker'], label: ['item-1'], source: ['item-2'] },
        axes: { rules: true, field_ink: 'secondary', camera_move: 'pan' },
        description: 'Ruled on a tinted field.' },
    ],
  },

  {
    id: 'slug-overlay',
    name: 'Where and when',
    role: 'overlay',
    kind: 'Lower third',
    description:
      'The documentary slug — a place and a date over the footage. Two supplied '
      + 'parts, which is what separates it from a bare label.',
    content: [
      { id: 'place', words: { min: 1, max: 4 }, max_chars: 34, description: 'Where this is.' },
      { id: 'date', words: { min: 1, max: 3 }, max_chars: 40, description: 'When. "March 1971", "Winter, 1911".' },
    ],
    variants: [
      { id: 'band', name: 'On the band', template: 'lower-third',
        slots: { place: ['title'], date: ['kicker'] },
        axes: { band_ink: 'dark', band_height: 0.18 },
        description: 'Place on the band, date above it.' },
      { id: 'band-wide', name: 'Wide band', template: 'lower-third',
        slots: { place: ['title'], date: ['kicker'] },
        axes: { band_ink: 'primary', band_width: 0.66, band_height: 0.2 },
        description: 'A wider, warmer band for a longer place name.' },
      { id: 'band-quiet', name: 'Quiet band', template: 'lower-third',
        slots: { place: ['title'], date: ['kicker'] },
        axes: { band_ink: 'secondary', band_height: 0.15, edge_roughness: 0.35 },
        description: 'Shallower and calmer, for when the footage carries the shot.' },
    ],
  },

  {
    id: 'citation-overlay',
    name: 'A source line',
    role: 'overlay',
    kind: 'Lower third',
    description:
      'Where a claim came from, small, over the footage. One supplied line — the '
      + 'figure it supports is a different shot.',
    content: [
      { id: 'citation', words: { min: 2, max: 8 }, max_chars: 34,
        description: 'The source. 34 is `lower-third`\'s title ceiling.' },
    ],
    variants: [
      { id: 'quiet', name: 'Quiet band', template: 'lower-third',
        slots: { citation: ['title'] },
        axes: { band_ink: 'secondary', band_height: 0.14, edge_roughness: 0.3 },
        description: 'The shallowest band — a citation should not shout.' },
      { id: 'dark', name: 'Dark band', template: 'lower-third',
        slots: { citation: ['title'] },
        axes: { band_ink: 'dark', band_height: 0.15 },
        description: 'Dark band for light footage.' },
      { id: 'narrow', name: 'Narrow band', template: 'lower-third',
        slots: { citation: ['title'] },
        axes: { band_ink: 'tertiary', band_width: 0.5, band_height: 0.14 },
        description: 'Pulled in from the edge, for a short source.' },
    ],
  },

  {
    id: 'call-to-action-overlay',
    name: 'Where to go next',
    role: 'overlay',
    kind: 'Lower third',
    description:
      'The end-card ask: one line and the place it points at. The destination is '
      + 'what a writer must supply beyond a phrase.',
    content: [
      { id: 'line', words: { min: 2, max: 6 }, max_chars: 34, description: 'The ask.' },
      { id: 'where', words: { min: 1, max: 3 }, max_chars: 40, description: 'The destination — a URL, a handle, a place.' },
    ],
    variants: [
      { id: 'band', name: 'On the band', template: 'lower-third',
        slots: { line: ['title'], where: ['kicker'] },
        axes: { band_ink: 'primary', band_height: 0.2 },
        description: 'Ask on the band, destination above.' },
      { id: 'band-dark', name: 'Dark band', template: 'lower-third',
        slots: { line: ['title'], where: ['kicker'] },
        axes: { band_ink: 'dark', band_height: 0.2, band_width: 0.64 },
        description: 'Darker and wider, for an end card that has the frame to itself.' },
      { id: 'band-quiet', name: 'Quiet band', template: 'lower-third',
        slots: { line: ['title'], where: ['kicker'] },
        axes: { band_ink: 'tertiary', band_height: 0.16, edge_roughness: 0.35 },
        description: 'Understated, for a mid-film aside rather than an end card.' },
    ],
  },
];

// =====================================================================================
// READING THE CATALOGUE
// =====================================================================================

const BY_ID: Record<string, ShotType> = Object.fromEntries(
  SHOT_TYPES.map((t) => [t.id, t]),
);

export function listShotTypes(): readonly ShotType[] {
  return SHOT_TYPES;
}

export function shotTypesForRole(role: ShotRole): readonly ShotType[] {
  return SHOT_TYPES.filter((t) => t.role === role);
}

export function getShotType(id: string): ShotType {
  const t = BY_ID[id];
  if (!t) {
    throw new Error(
      `unknown shot type "${id}" — known: ${SHOT_TYPES.map((x) => x.id).join(', ')}`,
    );
  }
  return t;
}

export function getShotTypeVariant(typeId: string, variantId: string): ShotTypeVariant {
  const type = getShotType(typeId);
  const v = type.variants.find((x) => x.id === variantId);
  if (!v) {
    throw new Error(
      `unknown variant "${variantId}" for shot type "${typeId}" — known: ${type.variants.map((x) => x.id).join(', ')}`,
    );
  }
  return v;
}

/** Pick a variant deterministically from a seed. Same (type, seed) → the same
 *  variant, forever; a caller that wants a specific one names it instead. */
export function selectShotTypeVariant(typeId: string, seed: string): ShotTypeVariant {
  const type = getShotType(typeId);
  const rng = makeCollageRng(seed, 'shot-type', type.id, 'variant');
  return type.variants[Math.floor(rng() * type.variants.length)];
}

/** The template a named (type, variant) reaches. This one call is the whole of
 *  `types.chosen-at-script`: name the type, get the layout. */
export function templateForShotType(typeId: string, variantId?: string, seed = 'default'): CollageTemplateDef {
  const v = variantId
    ? getShotTypeVariant(typeId, variantId)
    : selectShotTypeVariant(typeId, seed);
  return getCollageTemplate(v.template);
}

// =====================================================================================
// CONTENT → TEMPLATE SLOTS
// =====================================================================================

function words(s: string): string[] {
  return s.trim().split(/\s+/).filter(Boolean);
}

/** Distribute words across a variant's target slots, IN ORDER.
 *
 *  One target takes the whole line. Two or more split it as evenly as the count
 *  allows, with the remainder going to the LAST target — the band line, which
 *  is the one carrying the weight in every template that offers a pair. Two
 *  words over two targets is therefore one word each, which is Matt's case. */
export function distributeWords(ws: readonly string[], targets: readonly string[]): string[] {
  if (targets.length === 0) return [];
  if (targets.length === 1) return [ws.join(' ')];
  if (ws.length < targets.length) {
    throw new Error(
      `cannot split ${ws.length} word(s) across ${targets.length} slots — a slot would render empty`,
    );
  }
  const base = Math.floor(ws.length / targets.length);
  const extra = ws.length - base * targets.length;
  const out: string[] = [];
  let i = 0;
  for (let k = 0; k < targets.length; k++) {
    const take = base + (k === targets.length - 1 ? extra : 0);
    out.push(ws.slice(i, i + take).join(' '));
    i += take;
  }
  return out;
}

/** Lines onto targets, for a slot whose signature counts LINES.
 *
 *  The word distributor cannot serve this: it splits a run EVENLY, which is
 *  right for "two words, one per slot" and destroys a list — three bullets of
 *  1, 5 and 2 words would be re-cut into three equal piles and stop being the
 *  writer's items. Lines are the writer's own divisions and are never re-cut.
 *
 *  ONE target keeps them joined with newlines, which is how a `stacked`
 *  treatment receives a multi-line statement and folds it itself. MANY targets
 *  take one line each, and the counts must agree exactly — a list with more
 *  items than rows has nowhere to put the rest, and silently dropping one is
 *  the class of bug this file exists to prevent. */
export function distributeLines(ls: readonly string[], targets: readonly string[]): string[] {
  if (targets.length === 0) return [];
  if (targets.length === 1) return [ls.join('\n')];
  if (ls.length !== targets.length) {
    throw new Error(
      `cannot place ${ls.length} line(s) into ${targets.length} slots — they must agree exactly, `
      + 'or a line is dropped or a slot renders empty',
    );
  }
  return ls.map((l) => l);
}

/** The writer's own line divisions, trimmed, with blanks dropped. */
export function contentLines(given: string): string[] {
  return given.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
}

/** Check supplied content against a type's signature. Throws with the slot and
 *  the count, because a scripting pass needs to know WHICH line to shorten. */
export function validateShotTypeContent(
  type: ShotType, content: Record<string, string> = {},
): void {
  for (const id of Object.keys(content)) {
    if (!type.content.some((c) => c.id === id)) {
      throw new Error(
        `unknown content slot "${id}" for shot type "${type.id}" — known: ${type.content.map((c) => c.id).join(', ') || '(none — this type carries no language)'}`,
      );
    }
  }
  for (const slot of type.content) {
    const given = content[slot.id];
    if (given === undefined || given.trim() === '') {
      throw new Error(`shot type "${type.id}" needs content for "${slot.id}"`);
    }
    // WORD BOUNDS APPLY PER LINE once a slot counts lines, because that is what
    // the writer controls: a five-item list is five short items, not one budget
    // shared between them. For a single-line slot this is identical to the whole
    // string, so every type filed before this validates exactly as it did.
    const ls = contentLines(given);
    if (slot.lines) {
      if (ls.length < slot.lines.min || ls.length > slot.lines.max) {
        const want = slot.lines.min === slot.lines.max
          ? `exactly ${slot.lines.min}`
          : `${slot.lines.min}–${slot.lines.max}`;
        throw new Error(
          `shot type "${type.id}" slot "${slot.id}" takes ${want} line(s); got ${ls.length}`,
        );
      }
    } else if (ls.length > 1) {
      throw new Error(
        `shot type "${type.id}" slot "${slot.id}" is a single line; got ${ls.length}`,
      );
    }
    for (const line of ls) {
      const n = words(line).length;
      if (n < slot.words.min || n > slot.words.max) {
        const want = slot.words.min === slot.words.max
          ? `exactly ${slot.words.min}`
          : `${slot.words.min}–${slot.words.max}`;
        throw new Error(
          `shot type "${type.id}" slot "${slot.id}" takes ${want} word(s)`
          + `${slot.lines ? ' per line' : ''}; got ${n} ("${line}")`,
        );
      }
    }
    for (const line of (slot.lines ? ls : [given])) {
      if (line.length > slot.max_chars) {
        throw new Error(
          `shot type "${type.id}" slot "${slot.id}" holds ${slot.max_chars} chars max`
          + `${slot.lines ? ' per line' : ''} (got ${line.length})`,
        );
      }
    }
  }
}

/** Content, mapped onto the variant's template slots — INCLUDING the explicit
 *  '' drop for every template text slot the variant does not use.
 *
 *  The drop is not tidiness. `resolveTemplateText` renders an unmentioned
 *  OPTIONAL slot with its PLACEHOLDER, so a variant that quietly ignores
 *  `kicker` ships "CHAPTER ONE" into a finished frame. */
export function shotTypeTemplateText(
  type: ShotType, variant: ShotTypeVariant, content: Record<string, string> = {},
): Record<string, string> {
  validateShotTypeContent(type, content);
  const def = getCollageTemplate(variant.template);
  const out: Record<string, string> = {};

  for (const slot of type.content) {
    const targets = variant.slots[slot.id];
    if (!targets || targets.length === 0) {
      throw new Error(
        `variant "${variant.id}" of shot type "${type.id}" maps content slot "${slot.id}" to nothing`,
      );
    }
    const parts = slot.lines
      ? distributeLines(contentLines(content[slot.id]), targets)
      : distributeWords(words(content[slot.id]), targets);
    targets.forEach((target, k) => {
      if (!def.text_slots.some((s) => s.id === target)) {
        throw new Error(
          `variant "${variant.id}" maps "${slot.id}" to text slot "${target}", which template "${def.id}" does not have — known: ${def.text_slots.map((s) => s.id).join(', ') || '(none)'}`,
        );
      }
      out[target] = parts[k];
    });
  }

  for (const s of def.text_slots) {
    if (out[s.id] !== undefined) continue;
    if (s.required) {
      throw new Error(
        `variant "${variant.id}" of shot type "${type.id}" leaves required text slot "${s.id}" of template "${def.id}" unfilled`,
      );
    }
    out[s.id] = '';   // the drop
  }
  return out;
}

// =====================================================================================
// RENDERING — name a type, get a recipe, in either frame or both
// =====================================================================================

export interface RenderShotTypeInput {
  /** A `ShotType` id. */
  type: string;
  /** Content by the TYPE's slot ids (not the template's). */
  content?: Record<string, string>;
  /** A variant id; absent, one is drawn deterministically from the seed. */
  variant?: string;
  seed: string;
  aspect?: Aspect;
  skin?: string;
  elements?: Record<string, string[]>;
  slug?: string;
  duration_seconds?: number;
  bpm?: number;
  film_slug?: string | null;
}

export interface ShotTypeRender {
  type: ShotType;
  variant: ShotTypeVariant;
  aspect: Aspect;
  recipe: CollageRecipe;
}

/** THE SAMPLE INPUT a named type resolves to — the pure half of rendering, factored
 *  out so a caller that does its OWN composing can reach it.
 *
 *  This is what the send-to-Compositor bridge needs: `composeCollageFrames` already
 *  takes a `SampleTemplateInput` and produces both frames, so joining an accepted
 *  shot to the Compositor is resolving the type and handing the result over — not a
 *  second rendering path. Without this the bridge would have had to rebuild the
 *  text-mapping and axis wiring, which is where two implementations of one thing
 *  start.
 *
 *  `shotTypeTemplateText` is what makes it safe: it validates the content against the
 *  type's signature and explicitly drops every template slot the variant does not
 *  map, so a placeholder cannot leak into a composed frame. */
export function shotTypeSampleInput(
  input: Omit<RenderShotTypeInput, 'aspect'>,
): { type: ShotType; variant: ShotTypeVariant; sample: SampleTemplateInput } {
  const type = getShotType(input.type);
  const variant = input.variant
    ? getShotTypeVariant(type.id, input.variant)
    : selectShotTypeVariant(type.id, input.seed);
  return {
    type,
    variant,
    sample: {
      template: variant.template,
      skin: input.skin ?? DEFAULT_SHOT_TYPE_SKIN,
      seed: input.seed,
      slug: input.slug ?? `${type.id}-${variant.id}`,
      text: shotTypeTemplateText(type, variant, input.content),
      elements: input.elements,
      axes: variant.axes,
      duration_seconds: input.duration_seconds,
      bpm: input.bpm,
      film_slug: input.film_slug,
    },
  };
}

export function renderShotType(input: RenderShotTypeInput): ShotTypeRender {
  const type = getShotType(input.type);
  const variant = input.variant
    ? getShotTypeVariant(type.id, input.variant)
    : selectShotTypeVariant(type.id, input.seed);
  const aspect: Aspect = input.aspect ?? '16:9';

  const recipe = sampleTemplate({
    template: variant.template,
    skin: input.skin ?? DEFAULT_SHOT_TYPE_SKIN,
    seed: input.seed,
    slug: input.slug ?? `${type.id}-${variant.id}`,
    text: shotTypeTemplateText(type, variant, input.content),
    elements: input.elements,
    axes: variant.axes,
    aspect,
    duration_seconds: input.duration_seconds,
    bpm: input.bpm,
    film_slug: input.film_slug,
  });

  return { type, variant, aspect, recipe };
}

/** THE CLAUSE `types.content-flows-to-both`, as one call: the content is given
 *  ONCE and the same variant renders it in both frames. Same seed, same
 *  variant, two arrangements — never the wide one scaled.
 *
 *  THE BOTH-FRAMES ACT IS `sampleFrames`, NOT A SECOND COPY OF IT (Order 3.9992,
 *  §5f — *"any time we make a compositor shot, we automatically be making the 4K
 *  widescreen and the vertical versions"*). This function's only job is to turn
 *  a named TYPE into that call's input; which frames a shot owes, and the slug
 *  each one takes, are decided in one place. */
export function renderShotTypeInBothFrames(
  input: Omit<RenderShotTypeInput, 'aspect'>,
): { type: ShotType; variant: ShotTypeVariant; wide: CollageRecipe; vertical: CollageRecipe } {
  const type = getShotType(input.type);
  const variant = input.variant
    ? getShotTypeVariant(type.id, input.variant)
    : selectShotTypeVariant(type.id, input.seed);

  const [wide, vertical] = sampleFrames({
    template: variant.template,
    skin: input.skin ?? DEFAULT_SHOT_TYPE_SKIN,
    seed: input.seed,
    slug: input.slug ?? `${type.id}-${variant.id}`,
    text: shotTypeTemplateText(type, variant, input.content),
    elements: input.elements,
    axes: variant.axes,
    duration_seconds: input.duration_seconds,
    bpm: input.bpm,
    film_slug: input.film_slug,
  }, ['16:9', '9:16']);

  return { type, variant, wide, vertical };
}

// =====================================================================================
// ROLE, READ BACK OFF WHAT RENDERED
// =====================================================================================

/** Does this layer lay an opaque field under everything? — the recipe-grammar
 *  form of `hasSolidBackground` in delivery.ts. */
function isOpaqueGround(l: RecipeLayer): boolean {
  if (!l.ground || l.opacity < 1) return false;
  const kind = l.source?.kind;
  return kind === 'solid' || kind === 'gradient';
}

/** THE ROLE OF WHAT ACTUALLY RENDERED, not what the catalogue claimed.
 *
 *  §5d tells the exports apart by the solid-colour background and derives the
 *  kind rather than storing it. One layer up, the same reading: a full-frame
 *  opaque ground means the camera is covered, which is a cutaway; no ground
 *  means it shows through, which is an overlay. A declared role that disagrees
 *  with this is a defect, and the survey check asserts they agree. */
export function roleOfRecipe(recipe: CollageRecipe): ShotRole {
  // INSET IS CHECKED FIRST, because an inset also has an opaque ground and would
  // otherwise read as a cutaway. What separates them is the camera: a cutaway
  // covers the footage, an inset leaves a window into it. That is the same
  // reading `exportPlan` makes one level down, where material BELOW the camera
  // is what makes a stack an inset rather than an overlay.
  if (recipe.layers.some((l) => l.source?.kind === 'camera')) return 'inset';
  return recipe.layers.some(isOpaqueGround) ? 'cutaway' : 'overlay';
}
